import React, { useRef, useState } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLeadForm } from "./useLeadForm";
import { TextField, SelectField, Field, Honeypot, Consent, SubmitRow, SuccessState } from "./fields";
import { FORM_OPTIONS } from "../content/site";
import { EVENTS } from "../lib/analytics";

const INITIAL = { first_name: "", last_name: "", email: "", phone: "", desired_position: "", location: "", work_mode: "", experience: "", availability: "", resume_id: "", resume_name: "", consent: false, website: "" };

function ResumeUpload({ value, name, onUploaded, onClear, error }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
  const input = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setErr(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/leads/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.id, r.data.filename);
    } catch (e) { setErr(e?.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <Field id="resume" label="Resume" required error={error || err} hint="PDF or Word, up to 5 MB.">
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-[#1A6EFF]/30 bg-[#F0F4FF] px-4 h-12 text-sm" data-testid="resume-uploaded">
          <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4 text-[#1A6EFF]" />{name}</span>
          <button type="button" onClick={onClear} aria-label="Remove resume" className="p-1 rounded hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="resume-upload-btn"
          className="w-full h-12 rounded-lg border border-dashed border-[#1A6EFF]/40 bg-white text-sm text-[#1A6EFF] hover:bg-[#F0F4FF] flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-[#1A6EFF] outline-none">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4" />Upload resume</>}
        </button>
      )}
      <input ref={input} id="resume" name="resume_id" type="file" accept=".pdf,.doc,.docx" className="sr-only" onChange={(e) => pick(e.target.files?.[0])} data-testid="resume-file-input" />
    </Field>
  );
}

export default function CandidateForm() {
  const f = useLeadForm({ type: "candidate", initial: INITIAL, required: ["first_name", "last_name", "email", "desired_position", "resume_id"], onSuccessEvent: EVENTS.RESUME_SUBMIT });
  if (f.state === "success") {
    return <SuccessState testId="candidate-success" title="Application received." desc="Thank you. A recruiter will review your resume and reach out if there is a match for your experience and preferences." onReset={f.reset} />;
  }
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); f.submit((v) => ({
      first_name: v.first_name, last_name: v.last_name, email: v.email, phone: v.phone || null, resume_id: v.resume_id,
      details: { desired_position: v.desired_position, location: v.location, work_mode: v.work_mode, experience: v.experience, availability: v.availability },
    })); }} className="relative space-y-6" data-testid="candidate-form" aria-label="Candidate application form">
      <Honeypot value={f.values.website} onChange={(v) => f.set("website", v)} />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField id="first_name" label="First name" required value={f.values.first_name} onChange={(v) => f.set("first_name", v)} error={f.errors.first_name} autoComplete="given-name" />
        <TextField id="last_name" label="Last name" required value={f.values.last_name} onChange={(v) => f.set("last_name", v)} error={f.errors.last_name} autoComplete="family-name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField id="email" label="Email" type="email" required value={f.values.email} onChange={(v) => f.set("email", v)} error={f.errors.email} autoComplete="email" />
        <TextField id="phone" label="Phone" type="tel" value={f.values.phone} onChange={(v) => f.set("phone", v)} autoComplete="tel" />
      </div>
      <ResumeUpload value={f.values.resume_id} name={f.values.resume_name} error={f.errors.resume_id ? "Please upload your resume." : null}
        onUploaded={(id, nm) => { f.set("resume_id", id); f.set("resume_name", nm); }} onClear={() => { f.set("resume_id", ""); f.set("resume_name", ""); }} />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField id="desired_position" label="Desired position" required value={f.values.desired_position} onChange={(v) => f.set("desired_position", v)} error={f.errors.desired_position} />
        <TextField id="location" label="Location" value={f.values.location} onChange={(v) => f.set("location", v)} placeholder="City, State" />
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <SelectField id="work_mode" label="Work preference" value={f.values.work_mode} onChange={(v) => f.set("work_mode", v)} options={FORM_OPTIONS.workModes} />
        <SelectField id="experience" label="Years of experience" value={f.values.experience} onChange={(v) => f.set("experience", v)} options={FORM_OPTIONS.experience} />
        <SelectField id="availability" label="Availability" value={f.values.availability} onChange={(v) => f.set("availability", v)} options={FORM_OPTIONS.availability} />
      </div>
      <Consent checked={f.values.consent} onChange={(v) => f.set("consent", v)} error={f.errors.consent} />
      <SubmitRow state={f.state} errors={f.errors} label="Apply Now" testId="candidate-submit" />
    </form>
  );
}
